import { apiClient } from './secureApiClient.js';
import { logger } from '../../utils/logger';

/**
 * Loads all bread consumption logs using secure API
 * @returns {Promise<Array>} - Array of bread logs
 */
export const loadBreadLogs = async () => {
  try {
    logger.debug('Loading bread logs via secure API...');
    const logs = await apiClient.getBreadLogs();

    logger.debug('Bread logs loaded successfully via API:', logs.length);
    return logs;
  } catch (error) {
    logger.error('Error loading bread logs via API:', error);
    return [];
  }
};

/**
 * Creates a new bread consumption log using secure API
 * @param {Object} logData - Bread consumption data
 * @returns {Promise<Object|null>} - Created log or null if failed
 */
export const createBreadLog = async logData => {
  try {
    logger.debug('Creating bread log via API:', logData);
    const newLog = await apiClient.createBreadLog(logData);

    logger.debug('Bread log created successfully via API');
    return newLog;
  } catch (error) {
    logger.error('Error creating bread log via API:', error);
    return null;
  }
};

/**
 * Updates a bread consumption log using secure API
 * @param {string} logId - ID of the log to update
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object|null>} - Updated log or null if failed
 */
export const updateBreadLog = async (logId, updateData) => {
  try {
    logger.debug('Updating bread log via API:', logId, updateData);
    const updatedLog = await apiClient.updateBreadLog(logId, updateData);

    logger.debug('Bread log updated successfully via API');
    return updatedLog;
  } catch (error) {
    logger.error('Error updating bread log via API:', error);
    return null;
  }
};

/**
 * Calculates bread debt using secure API
 * @returns {Promise<Object|null>} - Debt calculation or null if failed
 */
export const calculateBreadDebt = async () => {
  try {
    logger.debug('Calculating bread debt via API...');
    const debtData = await apiClient.calculateBreadDebt();

    logger.debug('Bread debt calculated successfully via API');
    return debtData;
  } catch (error) {
    logger.error('Error calculating bread debt via API:', error);
    return null;
  }
};

/**
 * Marks a player's bread debt as paid using secure API
 * @param {string} playerId - ID of the player
 * @param {string} type - Type of payment (full/half)
 * @param {Object} userProfile - Current user profile
 * @param {Array} players - Current players array
 * @param {Function} setPlayers - State setter for players
 * @returns {Promise<boolean>} - Success status
 */
export const handleMarkPaid = async (playerId, type, userProfile) => {
  try {
    logger.debug('Marking bread debt as paid via API:', { playerId, type, userProfile });

    // Create a bread log entry to record the payment
    const paymentData = {
      playerId: playerId, // The player who ate the bread (cleared their debt)
      type: type === 'full' ? 'Full Slice Eaten' : 'Half Slice Eaten',
      action: 'consumed',
      slices: type === 'full' ? 1 : 0.5,
      note: `Debt cleared - ${type} slice eaten`,
    };
    
    logger.debug('Payment data being sent:', paymentData);

    const result = await apiClient.createBreadLog(paymentData);

    if (result) {
      logger.debug('Bread payment recorded successfully via API');
      // The API returns { breadLog: { ... } }, so we need to extract the inner object
      return result.breadLog || result;
    }

    return null;
  } catch (error) {
    logger.error('Error marking bread debt as paid via API:', error);
    return false;
  }
};
